import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ScrollText, Shield, Filter } from 'lucide-react';

export default function AuditLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => { load(); }, [entityFilter, page]);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      params.set('skip', page * 50);
      params.set('limit', '50');
      if (entityFilter) params.set('entity_type', entityFilter);
      const res = await api.get(`/api/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (!['admin','toplevman'].includes(user?.role)) return <div className="glass rounded-xl p-8 text-center"><Shield className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">Admin access required</p></div>;

  const entityTypes = ['lead', 'user', 'property', 'meeting', 'deal', 'payroll', 'config'];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div><h3 className="text-xl font-bold text-white">Audit Logs ({total})</h3><p className="text-dark-400 text-sm">Complete change history</p></div>
        <select value={entityFilter} onChange={e => { setEntityFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
          <option value="">All Types</option>
          {entityTypes.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div> :
      logs.length === 0 ? <div className="glass rounded-xl p-8 text-center"><ScrollText className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">No audit logs found</p></div> :
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-3 px-4 text-dark-400 font-medium">Time</th>
                <th className="text-left py-3 px-4 text-dark-400 font-medium">User</th>
                <th className="text-left py-3 px-4 text-dark-400 font-medium">Action</th>
                <th className="text-left py-3 px-4 text-dark-400 font-medium">Type</th>
                <th className="text-left py-3 px-4 text-dark-400 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="border-b border-dark-800 hover:bg-dark-800/30">
                  <td className="py-3 px-4 text-dark-300 text-xs whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="py-3 px-4 text-white">{log.user_name}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      log.action === 'create' ? 'bg-green-500/10 text-green-400' :
                      log.action === 'update' ? 'bg-blue-500/10 text-blue-400' :
                      log.action === 'soft_delete' ? 'bg-red-500/10 text-red-400' :
                      log.action === 'restore' ? 'bg-purple-500/10 text-purple-400' :
                      'bg-dark-500/10 text-dark-300'
                    }`}>{log.action}</span>
                  </td>
                  <td className="py-3 px-4 text-dark-300 capitalize">{log.entity_type}</td>
                  <td className="py-3 px-4 text-dark-400 text-xs max-w-xs truncate">
                    {log.details || (log.new_value ? JSON.stringify(log.new_value).substring(0, 80) : log.entity_id?.substring(0, 8))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 50 && (
          <div className="flex justify-center gap-2 p-4">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1 bg-dark-800 text-dark-300 rounded text-sm disabled:opacity-50">Prev</button>
            <span className="px-3 py-1 text-dark-400 text-sm">Page {page + 1}</span>
            <button onClick={() => setPage(page + 1)} disabled={(page + 1) * 50 >= total} className="px-3 py-1 bg-dark-800 text-dark-300 rounded text-sm disabled:opacity-50">Next</button>
          </div>
        )}
      </div>}
    </div>
  );
}
